import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  TextField,
  Divider,
  Chip,
} from "@mui/material";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

import { Contact } from "../../../interfaces/DTO/crm/contact";
import { ContactComment, ContactCommentType } from "../../../interfaces/DTO/crm/contactComment";
import { useAuthStore } from "../../../stores/authStore";
import {
  useContactCommentsByContactId,
  useCreateContactComment,
  useDeleteContactComment,
  usePartialUpdateContactComment,
} from "../../../hooks/crm/useContactComments";
import { formatName, formatIsoDateWithTime } from "../../../utils/namesFormater";
import { formatProductType } from "../../../utils/enumFormatter";
import { colorProductTypeUniversal, colorHandlerCode } from "../../../utils/colorEnumFormatter";
import { formatHandlerCode } from "../../../utils/namesFormater";

const COMMENT_TYPE_LABELS: Record<ContactCommentType, string> = {
  [ContactCommentType.GENERAL]: "כללי",
  [ContactCommentType.SMALLTALK]: "שאלה",
  [ContactCommentType.RISK_PROFILE]: "פרופיל סיכון",
};

interface Props {
  contacts: Contact[];
  isFamily: boolean;
  reportedPeriod?: string;
  productType?: number;
  handlerCode?: string;
  policyNumber?: string;
}

interface EditState {
  id: number;
  value: string;
}

const ContactCommentsPanel: React.FC<Props> = ({ contacts, isFamily, reportedPeriod, productType, handlerCode, policyNumber }) => {
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();

  // For family: selected contact to attribute the comment to
  const [selectedContactId, setSelectedContactId] = useState<number>(contacts[0]?.id);
  const [selectedType, setSelectedType] = useState<ContactCommentType>(ContactCommentType.GENERAL);
  const [newText, setNewText] = useState("");
  const [editState, setEditState] = useState<EditState | null>(null);
  const [dismissedHandler, setDismissedHandler] = useState(false);
  const [dismissedProduct, setDismissedProduct] = useState(false);
  const [dismissedPolicy, setDismissedPolicy] = useState(false);
  const [activeProductType, setActiveProductType] = useState<number | undefined>(productType);
  const [activeHandlerCode, setActiveHandlerCode] = useState<string | undefined>(handlerCode);

  // sync when props change (e.g. user selects a different row)
  useEffect(() => {
    setDismissedHandler(false);
    setDismissedProduct(false);
    setDismissedPolicy(false);
    setActiveProductType(productType);
    setActiveHandlerCode(handlerCode);
  }, [productType, handlerCode, policyNumber]);

  const { data: rawComments0 = [] } = useContactCommentsByContactId(contacts[0]?.id);
  const { data: rawComments1 = [] } = useContactCommentsByContactId(contacts[1]?.id);

  const rawComments = contacts.length > 1
    ? [...rawComments0, ...rawComments1]
    : rawComments0;

  const allComments = [...rawComments].sort((a, b) => {
    const dateA = new Date(a.created_at ?? 0).getTime();
    const dateB = new Date(b.created_at ?? 0).getTime();
    return dateA - dateB;
  });
  const create = useCreateContactComment();
  const remove = useDeleteContactComment();
  const update = usePartialUpdateContactComment();

  const selectedContact = contacts.find((c) => c.id === selectedContactId);
  const relatedDate = reportedPeriod ?? selectedContact?.last_data_reported_period ?? undefined;

  const handleCreate = () => {
    if (!newText.trim()) return;
    const comment = new ContactComment({
      contact_id: selectedContactId,
      user_id: user?.id,
      comment_type: selectedType,
      content: newText,
      ...(relatedDate ? { reported_period: relatedDate } : {}),
      ...(effectiveProductType != null ? { product_type: effectiveProductType } : {}),
      ...(effectiveHandlerCode != null ? { handler_code: effectiveHandlerCode } : {}),
      ...(effectivePolicyNumber != null ? { policy_number: effectivePolicyNumber } : {}),
    });
    create.mutate(comment, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["contactComments", "by", "contact_id", selectedContactId],
        });
      },
    });
    setNewText("");
  };

  const handleEditSave = (id: number) => {
    if (!editState) return;
    update.mutate({ id, fields: { content: editState.value } });
    setEditState(null);
  };

  const effectiveHandlerCode = dismissedHandler ? undefined : activeHandlerCode;
  const effectiveProductType = dismissedProduct ? undefined : activeProductType;
  const effectivePolicyNumber = dismissedPolicy ? undefined : policyNumber;

  return (
    <Box sx={{ p: 2, width: "100%", direction: "rtl" }}>
      {(effectiveHandlerCode || effectiveProductType != null || effectivePolicyNumber) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
          {effectiveHandlerCode && (
            <Chip
              label={formatHandlerCode(effectiveHandlerCode)}
              size="small"
              sx={{ backgroundColor: colorHandlerCode(effectiveHandlerCode), color: "#fff" }}
              onDelete={() => setDismissedHandler(true)}
            />
          )}
          {effectiveProductType != null && (
            <Chip
              label={formatProductType(effectiveProductType)}
              size="small"
              sx={{ backgroundColor: colorProductTypeUniversal(effectiveProductType), color: "#fff" }}
              onDelete={() => setDismissedProduct(true)}
            />
          )}
          {effectivePolicyNumber && (
            <Chip
              label={effectivePolicyNumber}
              size="small"
              variant="outlined"
              onDelete={() => setDismissedPolicy(true)}
            />
          )}
        </Stack>
      )}
      {/* ── New comment form ── */}
      <Box
        sx={{
          mb: 3,
          backgroundColor: "#e5ddd5",
          borderRadius: 3,
          p: 2,
          backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0icGF0dGVybiIgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gMCw1MCBMIDUwLDAgTCAxMDAsNTAgTCA1MCwxMDAgWiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZDlkOWQ5IiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4yIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3BhdHRlcm4pIi8+PC9zdmc+')",
        }}
      >
        {/* Chips row: contact (if family) + comment type */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
          {isFamily && contacts.length > 1 && (
            <FormControl size="small">
              <Select
                value={selectedContactId}
                onChange={(e) => setSelectedContactId(Number(e.target.value))}
                renderValue={(val) => {
                  const c = contacts.find((c) => c.id === val);
                  return (
                    <Chip label={c ? formatName(c) : val} size="small" color="default" />
                  );
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-select": { p: 0 },
                  bgcolor: "transparent",
                }}
              >
                {contacts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatName(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small">
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as ContactCommentType)}
              renderValue={(val) => (
                <Chip label={COMMENT_TYPE_LABELS[val as ContactCommentType]} size="small" color="primary" />
              )}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-select": { p: 0 },
                bgcolor: "transparent",
              }}
            >
              {Object.values(ContactCommentType).map((t) => (
                <MenuItem key={t} value={t}>
                  {COMMENT_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {relatedDate && (
            <Chip label={`תקופה: ${relatedDate}`} size="small" variant="outlined" />
          )}
        </Stack>

        {/* Input row */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <IconButton
            onClick={handleCreate}
            disabled={!newText.trim() || create.isPending}
            sx={{
              backgroundColor: "#25d366",
              color: "#fff",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#20ba5a" },
              "&:disabled": { backgroundColor: "#e0e0e0", color: "#9e9e9e" },
            }}
          >
            <SendIcon />
          </IconButton>
          <TextField
            multiline
            maxRows={5}
            fullWidth
            placeholder="הקלד הערה..."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleCreate();
              }
            }}
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              "& .MuiOutlinedInput-root": { borderRadius: 3 },
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* ── Comments list grouped by reported_period ── */}
      {allComments.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          אין הערות עדיין
        </Typography>
      )}

      {(() => {
        // Group by reported_period (null → "ללא תקופה"), sorted DESC by period
        const groups: Record<string, typeof allComments> = {};
        allComments.forEach((c) => {
          const key = c.reported_period ?? "ללא תקופה";
          if (!groups[key]) groups[key] = [];
          groups[key].push(c);
        });
        const sortedKeys = Object.keys(groups).sort((a, b) => {
          if (a === "ללא תקופה") return 1;
          if (b === "ללא תקופה") return -1;
          return b.localeCompare(a);
        });

        return sortedKeys.map((period) => (
          <Box key={period} sx={{ mb: 3 }}>
            {/* Period header */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: "#d1e8d1",
                  borderRadius: 3,
                  px: 2,
                  py: 0.5,
                  color: "#3d6b3d",
                  fontWeight: 600,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                {period}
              </Typography>
            </Box>

            {/* Chat area */}
            <Box
              sx={{
                backgroundColor: "#e5ddd5",
                borderRadius: 3,
                p: 2,
                direction: "ltr",
                backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0icGF0dGVybiIgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gMCw1MCBMIDUwLDAgTCAxMDAsNTAgTCA1MCwxMDAgWiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZDlkOWQ5IiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4yIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3BhdHRlcm4pIi8+PC9zdmc+')",
              }}
            >
              <Stack spacing={1.5}>
                {groups[period].map((comment) => (
                  <Box
                    key={comment.id}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      "&:hover .comment-actions": { visibility: "visible" },
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "85%",
                        backgroundColor: "#dcf8c6",
                        borderRadius: 2,
                        px: 1.5,
                        pt: 1,
                        pb: 0.5,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          right: -8,
                          width: 0,
                          height: 0,
                          borderStyle: "solid",
                          borderWidth: "0 10px 10px 0",
                          borderColor: "transparent #dcf8c6 transparent transparent",
                        },
                      }}
                    >
                      {/* Content or edit field */}
                      {editState?.id === comment.id ? (
                        <TextField
                          multiline
                          minRows={2}
                          fullWidth
                          value={editState.value}
                          onChange={(e) => setEditState({ id: comment.id, value: e.target.value })}
                          size="small"
                          inputProps={{ style: { textAlign: "right", direction: "rtl" } }}
                          inputRef={(el) => {
                            if (el) {
                              el.focus();
                              el.setSelectionRange(el.value.length, el.value.length);
                            }
                          }}
                          sx={{ mb: 0.5 }}
                        />
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, direction: "rtl" }}>
                          <Tooltip title="ערוך">
                            <NoteAltOutlinedIcon
                              fontSize="small"
                              sx={{ color: "text.secondary", cursor: "pointer", mt: "2px", flexShrink: 0 }}
                              onClick={() => setEditState({ id: comment.id, value: comment.content })}
                            />
                          </Tooltip>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap", textAlign: "right", cursor: "pointer" }}
                            onClick={() => setEditState({ id: comment.id, value: comment.content })}
                          >
                            {comment.content}
                          </Typography>
                        </Box>
                      )}

                      {/* Footer: type + time + actions */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5, direction: "rtl" }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          {contacts.length > 1 && (() => {
                            const c = contacts.find((c) => c.id === comment.contact_id);
                            return c ? (
                              <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 700, color: "secondary.main" }}>
                                {c.first_name} ·
                              </Typography>
                            ) : null;
                          })()}
                          {comment.handler_code && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                              {formatHandlerCode(comment.handler_code)} ·
                            </Typography>
                          )}
                          {comment.product_type != null && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                              {formatProductType(comment.product_type)} ·
                            </Typography>
                          )}
                          {comment.policy_number && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                              {comment.policy_number} ·
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            {COMMENT_TYPE_LABELS[comment.comment_type]}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            · {comment.created_at && formatIsoDateWithTime(comment.created_at)}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0}
                          alignItems="center"
                          className="comment-actions"
                          sx={{ visibility: editState?.id === comment.id ? "visible" : "hidden" }}
                        >
                          {editState?.id === comment.id ? (
                            <>
                              <Tooltip title="שמור">
                                <IconButton size="small" onClick={() => handleEditSave(comment.id)}>
                                  <CheckIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="בטל">
                                <IconButton size="small" onClick={() => setEditState(null)}>
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="ערוך">
                              <IconButton size="small" onClick={() => setEditState({ id: comment.id, value: comment.content })}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="מחק">
                            <IconButton size="small" onClick={() => remove.mutate(comment.id)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        ));
      })()}
    </Box>
  );
};

export default ContactCommentsPanel;
